/**
 * Приложение просмотра заказа для клиента.
 *
 * URL: /client/orders/{uid}
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { getOrderByUid, cancelOrder } from './api/client';
import { OrderDetails } from './components/OrderDetails';
import { CancelButton } from './components/CancelButton';
import { LoadingSkeleton } from './components/LoadingSkeleton';
import { ErrorPage } from './components/ErrorPage';
import { Toast, toastStyles } from './components/Toast';
import { reachGoal } from './utils/metrika';

// Получаем UID из URL
function getUidFromUrl() {
  const pathParts = window.location.pathname.split('/');
  // URL: /client/orders/{uid}
  const ordersIndex = pathParts.indexOf('orders');
  if (ordersIndex !== -1 && pathParts[ordersIndex + 1]) {
    return pathParts[ordersIndex + 1];
  }
  return null;
}

function App() {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const isFirstLoad = useRef(true);

  const uid = getUidFromUrl();

  const fetchOrder = useCallback(async (silent = false) => {
    if (!uid) {
      setError('Ссылка на заказ недействительна');
      setLoading(false);
      return;
    }

    if (!silent) {
      setLoading(true);
    }

    try {
      setError(null);
      const data = await getOrderByUid(uid);
      setOrder(data);
      
      if (isFirstLoad.current) {
        reachGoal('view_order', {
          order_id: uid,
          order_status: data.status,
          order_type: data.order_type,
        });
        isFirstLoad.current = false;
      }
    } catch (err) {
      console.error('Error fetching order:', err);
      if (err.response?.status === 404) {
        setError('Заказ не найден');
      } else if (!silent) {
        setError(err.response?.data?.error || 'Не удалось загрузить заказ');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [uid]);

  useEffect(() => {
    fetchOrder();

    // Автообновление каждые 30 секунд для отслеживания статуса
    const interval = setInterval(() => fetchOrder(true), 30000);
    return () => clearInterval(interval);
  }, [fetchOrder]);

  const handleCancel = async (reason) => {
    try {
      setActionLoading(true);
      const updatedOrder = await cancelOrder(uid, reason);
      setOrder(updatedOrder);

      reachGoal('cancel_order_client', {
        order_id: uid,
        order_type: order?.order_type,
        order_status: order?.status,
        has_refund: updatedOrder.refund?.success || false,
      });

      const hasRefund = updatedOrder.refund?.success && updatedOrder.order_type !== 'booking_no_prepayment';
      setToast({
        message: hasRefund
          ? (updatedOrder.refund?.message || 'Заказ отменён. Ожидайте возврата средств в течение 3 дней.')
          : 'Заказ отменён',
        type: 'success',
      });
    } catch (err) {
      console.error('Error cancelling order:', err);
      setToast({
        message: err.response?.data?.error || 'Не удалось отменить заказ',
        type: 'error',
      });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (error) {
    return <ErrorPage message={error} onRetry={fetchOrder} />;
  }

  return (
    <>
      {/* Toast styles */}
      <style>{toastStyles}</style>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-6 px-4 pb-24">
        <div className="max-w-lg mx-auto space-y-6">
          {/* Детали заказа (включая статус в заголовке) */}
          {order && <OrderDetails order={order} />}

          {/* Автообновление */}
          <div className="text-center text-xs text-gray-400 dark:text-gray-500">
            Статус обновляется автоматически
          </div>
        </div>
      </div>

      {/* Закреплённая кнопка отмены внизу экрана */}
      {order && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm border-t border-gray-200 dark:border-gray-700 p-4 z-40">
          <div className="max-w-lg mx-auto">
            <CancelButton
              order={order}
              onCancel={handleCancel}
              isLoading={actionLoading}
            />
          </div>
        </div>
      )}

      {/* Toast уведомление */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
}

export default App;
